import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa6";
import BridgeBG from "../../img/bridgeBG.avif";
import { useDownloadAppModal } from "../../context/DownloadAppModalContext";

// ─── Types ───────────────────────────────────────────────────────────────────

type Letter = "a" | "b" | "c" | "d" | "e" | "f" | "g" | "h";
type FrameNumber = 1 | 2 | 3 | 4;

type FrameData = {
    top?: number | string;
    left?: number | string;
    right?: number | string;
    bottom?: number | string;
    width: number | string;
    src: string;
};

// ─── AnimatedFrames ───────────────────────────────────────────────────────────

export function AnimatedFrames() {
    const [frame, setFrame] = useState<FrameNumber>(1);
    const [isMobile, setIsMobile] = useState(false);
    const [isSmallMobile, setIsSmallMobile] = useState(false);

    useEffect(() => {
        const checkSize = () => {
            const w = window.innerWidth;
            setIsMobile(w < 768);
            setIsSmallMobile(w <= 400);
        };
        checkSize();
        window.addEventListener("resize", checkSize);
        return () => window.removeEventListener("resize", checkSize);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setFrame((prev) => (prev === 4 ? 1 : ((prev + 1) as FrameNumber)));
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const allFrames: Record<FrameNumber, Record<Letter, FrameData>> = {
        1: {
            a: { top: "0%", left: "0%", width: 135, src: "/images/1a.png" },
            b: { top: "14.878%", left: "0%", width: 150, src: "/images/1b.png" },
            c: { top: "54.197%", left: "6.312%", width: 140, src: "/images/1c.png" },
            d: { top: "54.197%", left: "23.214%", width: 150, src: "/images/1d.png" },
            e: { top: "54.197%", left: "68.929%", width: 150, src: "/images/1e.png" },
            f: { top: "17.005%", left: "73.650%", width: 141, src: "/images/1f.png" },
            g: { top: "0%", left: "69.579%", width: 121, src: "/images/1g.png" },
            h: { top: "0%", left: "33.276%", width: 150, src: "/images/1h.png" },
        },
        2: {
            a: { top: "22.447%", left: "0%", width: 134, src: "/images/2a.png" },
            b: { top: "63.762%", left: "13.678%", width: 150, src: "/images/2b.png" },
            c: { top: "66.420%", left: "38.138%", width: 141, src: "/images/2c.png" },
            d: { top: "63.762%", left: "65.753%", width: 150, src: "/images/2d.png" },
            e: { top: "17.005%", left: "67.075%", width: 150, src: "/images/2e.png" },
            f: { top: "0%", left: "54.584%", width: 141, src: "/images/2f.png" },
            g: { top: "0%", left: "38.138%", width: 121, src: "/images/2g.png" },
            h: { top: "0%", left: "19.735%", width: 150, src: "/images/2h.png" },
        },
        3: {
            a: { top: "0%", left: "0%", width: 134, src: "/images/3a.png" },
            b: { top: "21.258%", left: "10.523%", width: 150, src: "/images/3b.png" },
            c: { top: "66.420%", left: "0%", width: 141, src: "/images/3c.png" },
            d: { top: "55.798%", left: "48.005%", width: 150, src: "/images/3d.png" },
            e: { top: "66.420%", left: "76.286%", width: 150, src: "/images/3e.png" },
            f: { top: "8.503%", left: "65.753%", width: 141, src: "/images/3f.png" },
            g: { top: "0%", left: "76.286%", width: 121, src: "/images/3g.png" },
            h: { top: "0%", left: "36.692%", width: 150, src: "/images/3h.png" },
        },
        4: {
            a: { top: "6.376%", left: "15.786%", width: 134, src: "/images/4a.png" },
            b: { top: "21.258%", left: "0%", width: 150, src: "/images/4b.png" },
            c: { top: "53.135%", left: "13.807%", width: 141, src: "/images/4c.png" },
            d: { top: "66.420%", left: "41%", width: 150, src: "/images/4d.png" },
            e: { top: "61.117%", left: "76.286%", width: 150, src: "/images/4e.png" },
            f: { top: "23.911%", left: "76.286%", width: 141, src: "/images/4f.png" },
            g: { top: "11.957%", left: "61.811%", width: 121, src: "/images/4g.png" },
            h: { top: "0%", left: "41.419%", width: 150, src: "/images/4h.png" },
        },
    };

    const mobileFrames: Record<FrameNumber, Partial<Record<Letter, FrameData>>> = {
        1: { a: allFrames[1].a, g: allFrames[1].g, c: { ...allFrames[1].c, left: "50%" } },
        2: { b: allFrames[2].b, d: allFrames[2].d, c: { ...allFrames[2].c, left: "50%" } },
        3: { a: allFrames[3].a, g: allFrames[3].g, d: { ...allFrames[3].d, left: "50%" } },
        4: { b: allFrames[4].b, e: allFrames[4].e, d: { ...allFrames[4].d, left: "50%" } },
    };

    // On screens ≤ 400px, scale images down to ~half size
    const SIZE_MULTIPLIER = isSmallMobile ? 0.5 : 1;

    const frames = isMobile ? mobileFrames : allFrames;

    return (
        <div className="absolute w-full h-full overflow-hidden">
            {(Object.keys(frames[frame]) as Letter[]).map((letter) => {
                const data = frames[frame][letter];
                if (!data) return null;

                const scaledWidth =
                    typeof data.width === "number"
                        ? data.width * SIZE_MULTIPLIER
                        : data.width;

                return (
                    <motion.img
                        key={letter}
                        src={data.src}
                        className="absolute"
                        style={{
                            width: scaledWidth,
                            transform: data.left === "50%" ? "translateX(-50%)" : undefined,
                        }}
                        animate={{ top: data.top, left: data.left }}
                        transition={{ duration: 1.2, ease: "easeInOut" }}
                    />
                );
            })}
        </div>
    );
}

// ─── BridgeCTASection ─────────────────────────────────────────────────────────

export default function BridgeCTASection() {
    const { openDownloadModal } = useDownloadAppModal();

    const container = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const fadeUp = {
        hidden: {
            opacity: 0,
            y: 40,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
            },
        },
    };

    return (
        <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="relative max-w-7xl mx-auto
                       h-[55vh] min-h-[320px]
                       md:h-screen
                       bg-white flex items-center justify-center
                       my-10 md:my-20"
            style={{
                backgroundImage: `url(${BridgeBG})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <motion.div
                variants={fadeUp}
                className="max-w-90 md:max-w-[692px] flex flex-col items-center gap-4 md:gap-6 text-center"
            >
                <div className="bg-white flex items-center gap-2 border border-[#E2E8F0] text-[#2C3036] text-xs md:text-sm font-medium shadow-sm px-3 py-2 md:px-4 md:py-2.5 rounded-3xl">
                    <div className="w-2 h-2 bg-[#ECAA40] rounded-full" />
                    <p>GROW FINANCIALLY WITH US</p>
                    <FaArrowRight size={14} color="#637381" />
                </div>
                <h2 className="text-[#0F172B] text-xl xs:text-3xl md:text-6xl font-semibold">
                    Bridge the Distance with Every Transfer
                </h2>
                <p className="text-[#637381] text-sm xs:text-base md:text-2xl">
                    Create Your International Account Instantly—Start Banking Globally
                </p>
                {/* <button
                    className="w-[174px] h-13 rounded-lg bg-[#0B7F47] border-2 border-[#7FBC51] shadow-sm text-white text-sm p-3.5 cursor-pointer transition-colors duration-300 hover:bg-[#0B7F47]/60"
                    onClick={openDownloadModal}
                >
                    Get started — it's free
                </button> */}
            </motion.div>

            <AnimatedFrames />
        </motion.div>
    );
}